import { FC } from "react";
import { Button, Form } from "antd";
import { MdCheck } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { useHotelDynamicDetails } from "src/Modules/Hotels/Pages/HotelDetails/context";
import GuestTabs from "../GuestTabs/GuestTabs";
import GuestFields, { GuestHeaderFields } from "../GuestFields/GuestFields";
import RateConditions from "../RateConditions/RateConditions";
import type { Guest } from "../../utils";

interface GuestsFormListProps {
  form: any;
  guests: Guest[];
  activeKey: string | null;
  onActiveKeyChange: (key: string) => void;
  onPrev: () => void;
  onNext: () => void;
}

const GuestsFormList: FC<GuestsFormListProps> = ({ form, guests, activeKey, onActiveKeyChange, onPrev, onNext }) => {
  const { t } = useTranslation();
  const { policiesAcceptedState, rateConditions, isRateConditionsFetching } = useHotelDynamicDetails();
  const [policiesAccepted] = policiesAcceptedState;
  const hasRateConditions = Boolean(!isRateConditionsFetching && rateConditions?.length);
  const activeIndex = Math.max(
    guests.findIndex((guest) => guest.key === activeKey),
    0,
  );
  const activeGuest = guests[activeIndex];
  const isFirst = activeIndex === 0;
  const isLast = activeIndex === guests.length - 1;
  const shouldRenderTabs = guests.length > 1;  const isActiveGuestLead = Form.useWatch(activeGuest ? [activeGuest.key, "isLeadGuest"] : [], { form, preserve: true });

  if (!activeGuest) return null;

  return (
    <div className="flex flex-col gap-5">
      {shouldRenderTabs && (
        <div>
          <GuestTabs guests={guests} activeKey={activeKey} onChange={onActiveKeyChange} form={form} />
        </div>
      )}
      <div className="bg-background-default flex flex-col gap-4 rounded-sm p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2 text-lg font-semibold">
            <div>{t("Flights.details.passengers.passengerNumber", { number: activeGuest.indexInType + 1 })}</div>
            <div>({t(`Flights.details.passengerType.${activeGuest.type}`)})</div>
            <span className="flex items-center gap-1.5 rounded-full border border-border-default px-3 py-1 text-xs font-semibold uppercase tracking-[0.5px] text-font-subtle">
              {t("Hotels.search.roomNumber", { number: activeGuest.roomIndex + 1 })}
            </span>
            {isActiveGuestLead && (
              <div
                className="p-1! text-xs px-4! rounded-full! flex! items-center! gap-2 text-font-inverted! bg-background-primary"
              >
                <div><MdCheck className="w-4 h-4" /></div>
                {t("Hotels.guests.primaryGuest")}
              </div>
            )}
          </div>
          {guests.map((guest) => (
            <div key={guest.key} style={{ display: guest.key === activeKey ? "contents" : "none" }}>
              <GuestHeaderFields guest={guest} guests={guests} form={form} />
            </div>
          ))}
        </div>
        <div>
          {guests.map((guest) => (
            <div key={guest.key} style={{ display: guest.key === activeKey ? "block" : "none" }}>
              <GuestFields guest={guest} guests={guests} form={form} />
            </div>
          ))}
        </div>
        <Form.Item
          name="policiesAccepted"
          valuePropName="checked"
          trigger="onCheckedChange"
          initialValue={policiesAccepted}
          className="m-0!"
          rules={[
            {
              validator: (_, value) =>
                !hasRateConditions || value ? Promise.resolve() : Promise.reject(t("Hotels.guests.policiesRequired")),
            },
          ]}
        >
          <RateConditions />
        </Form.Item>
        <div className="flex items-center">
          {!isFirst && (
            <Button type="default" className="w-30 p-5! bg-background-default! border-border-default!" onClick={onPrev}>
              {t("Flights.details.passengers.previous")}
            </Button>
          )}
          {!isLast && (
            <Button type="primary" className="w-30 p-5! ms-auto" htmlType="button" onClickCapture={onNext}>
              {t("Flights.details.passengers.next")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestsFormList;
